import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Eyebrow, PageHead, Tile, AreaLine, Empty, fmtMins, fmtTime } from "@/components/admin/ui";
import { BarChart3, Trophy, Clock } from "lucide-react";

export default function Analytics() {
  const [a, setA] = useState(null);

  useEffect(() => {
    document.title = "Analytics · Udyog Classes";
    api
      .get("/analytics")
      .then(({ data }) => setA(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!a) return <div className="py-20 text-center text-white/35">Crunching your classes…</div>;
  if ((a.classes || []).length === 0)
    return (
      <div className="space-y-6">
        <PageHead eyebrow="Analytics" title="Trends" sub="Run a class or two and this page fills up." />
        <Empty icon={BarChart3} text="No class data yet." testid="analytics-empty" />
      </div>
    );

  const t = a.totals;
  const maxJoined = Math.max(...a.classes.map((c) => c.joined), 1);
  const bestSlot = [...a.slots].sort((x, y) => y.avg_joined - x.avg_joined)[0];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <PageHead
        eyebrow="Analytics"
        title="What's working"
        sub={`${t.classes_held} classes analysed`}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile testid="an-avg-joined" label="Avg. turnout" value={t.avg_joined} hint="students per class" />
        <Tile
          testid="an-retention"
          label="Avg. retention"
          value={`${t.avg_retention}%`}
          tone={t.avg_retention >= 70 ? "good" : "warn"}
          hint="stay till the end"
        />
        <Tile testid="an-duration" label="Avg. class length" value={fmtMins(t.avg_duration)} />
        <Tile
          testid="an-best-slot"
          label="Best time slot"
          value={bestSlot ? bestSlot.label.split("–")[0] : "—"}
          hint={bestSlot ? `${bestSlot.avg_joined} avg students` : ""}
        />
      </div>

      <Card className="p-5" testid="analytics-trend">
        <div className="flex items-center justify-between mb-3">
          <Eyebrow>Turnout over time</Eyebrow>
          <span className="text-[10.5px] text-white/35">last {a.classes.length} classes</span>
        </div>
        <AreaLine points={a.classes.map((c) => c.joined)} height={160} testid="trend-chart" />
        <div className="flex items-center justify-between text-[10.5px] text-white/35 mt-1">
          <span>{fmtTime(a.classes[0].at)}</span>
          <span>Peak {maxJoined}</span>
          <span>{fmtTime(a.classes[a.classes.length - 1].at)}</span>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5" testid="analytics-classes">
          <Eyebrow className="mb-4">Class by class</Eyebrow>
          <div className="space-y-3 max-h-[420px] overflow-y-auto no-scrollbar">
            {[...a.classes].reverse().map((c) => (
              <Link
                key={c.room_name}
                to={`/admin/classes/${c.room_name}`}
                className="block group"
                data-testid={`an-class-${c.room_name}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12.5px] text-white/80 truncate group-hover:text-emerald-300">{c.title}</span>
                  <span className="text-[11px] text-white/40 whitespace-nowrap">
                    {c.joined} joined · {c.retention_pct}% stayed
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
                  <span
                    className="block h-full bg-gradient-to-r from-emerald-600 to-emerald-400"
                    style={{ width: `${(c.joined / maxJoined) * 100}%` }}
                  />
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <div className="space-y-5">
          <Card className="p-5" testid="analytics-slots">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-3.5 h-3.5 text-emerald-400/80" />
              <Eyebrow>When students show up (IST)</Eyebrow>
            </div>
            <div className="space-y-2.5">
              {a.slots.map((s) => (
                <div key={s.hour} className="flex items-center gap-3" data-testid={`slot-${s.hour}`}>
                  <span className="text-[11.5px] text-white/50 w-24 shrink-0">{s.label}</span>
                  <span className="flex-1 h-2 rounded-full bg-white/[0.07] overflow-hidden">
                    <span
                      className="block h-full bg-emerald-400"
                      style={{
                        width: `${(s.avg_joined / Math.max(...a.slots.map((x) => x.avg_joined), 1)) * 100}%`,
                      }}
                    />
                  </span>
                  <span className="text-[11px] text-white/45 w-20 text-right">
                    {s.avg_joined} avg · {s.classes}x
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5" testid="analytics-weeks">
            <Eyebrow className="mb-4">New vs returning students</Eyebrow>
            <div className="space-y-2.5">
              {a.weeks.map((w) => {
                const total = Math.max(1, w.new + w.repeat);
                return (
                  <div key={w.week} className="flex items-center gap-3" data-testid={`week-${w.week}`}>
                    <span className="text-[11px] text-white/40 w-20 shrink-0">
                      {new Date(w.week).toLocaleDateString([], { day: "numeric", month: "short" })}
                    </span>
                    <span className="flex-1 h-2.5 rounded-full overflow-hidden flex bg-white/[0.05]">
                      <span className="h-full bg-emerald-400" style={{ width: `${(w.new / total) * 100}%` }} />
                      <span className="h-full bg-emerald-800" style={{ width: `${(w.repeat / total) * 100}%` }} />
                    </span>
                    <span className="text-[11px] text-white/45 w-24 text-right">
                      {w.new} new · {w.repeat} back
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {t.best_class && (
            <Card className="p-5" testid="analytics-best">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-3.5 h-3.5 text-amber-300" />
                <Eyebrow>Your best class so far</Eyebrow>
              </div>
              <Link
                to={`/admin/classes/${t.best_class.room_name}`}
                className="font-display text-lg hover:text-emerald-300"
              >
                {t.best_class.title}
              </Link>
              <div className="text-[11.5px] text-white/45 mt-1">
                {t.best_class.joined} joined · peak {t.best_class.peak} · {t.best_class.retention_pct}% stayed till the
                end
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
